import React, { useEffect, useState } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import { formatEuroCents, type EuroCents } from '../../types/branded';
import { db } from '../../db/schema';
import { loadFaelligkeiten, type VguFaelligkeit } from '../../services/vorsorgeService';
import { loadRechnungen } from '../../services/rechnungenService';
import { getPraeventionKpi, type PraeventionKpi } from '../../services/preventionService';
import { isDriveConnected, getLastBackupTime, checkDriveNewerBackup, downloadLatestDriveBackup } from '../../services/googleDriveBackup';
import { isSyncConfigured } from '../../services/syncService';
import { importDexieEncrypted } from '../../services/backupService';
import { offeneMaengel, PRIORITAET_META, type OffenerMangel } from '../../services/begehungService';
import { loadAkte } from '../../services/akteService';
import { loadOffeneErinnerungen, toggleErinnerungErledigt } from '../../services/erinnerungService';
import type { AkteEintrag, Erinnerung } from '../../db/schema';
import type { Rechnung } from '../abrechnung/types';
import type { ModuleKey } from '../../components/layout/AppLayout';

// ── Live-KPIs aus Dexie ────────────────────────────────────────────────────

interface DashboardKpis {
  vorsorgenFaellig:   number;
  vorsorgenUeberfaellig: number;
  offeneRechnungen:   number;
  gemahntRechnungen:  number;
  heuteZeitMin:       number;
  umsatzMonat:        number;  // EuroCents
}

async function loadKpis(mandantId?: string): Promise<DashboardKpis> {
  const now        = new Date();
  const today      = now.toISOString().slice(0, 10);
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

  // VGÜ-Fälligkeiten aus vorsorgeService (primäre Quelle)
  const vguAll  = await loadFaelligkeiten(mandantId);
  const faellig      = vguAll.filter(v => v.status === 'OFFEN' || v.status === 'UEBERFAELLIG').length;
  const ueberfaellig = vguAll.filter(v => v.status === 'UEBERFAELLIG').length;

  // Rechnungen aus rechnungenService
  const rechnungenAll = await loadRechnungen();
  const rechnGefilt = mandantId
    ? rechnungenAll.filter(r => String(r.mandantId) === mandantId)
    : rechnungenAll;
  const offen   = rechnGefilt.filter(r => r.status === 'gestellt' || r.status === 'gemahnt').length;
  const gemahnt = rechnGefilt.filter(r => r.status === 'gemahnt').length;
  const umsatz  = rechnGefilt
    .filter(r => r.status === 'bezahlt' && r.datum >= monthStart)
    .reduce((s, r) => s + r.summeBrutto, 0);

  // Heute erfasste Zeit
  const heuteZeit = await db.zeiteintraege.where('datum').equals(today).toArray();
  const heuteMin  = (mandantId
    ? heuteZeit.filter(z => z.mandantId === mandantId)
    : heuteZeit
  ).reduce((s, z) => s + z.dauerMin, 0);

  return {
    vorsorgenFaellig:      faellig,
    vorsorgenUeberfaellig: ueberfaellig,
    offeneRechnungen:      offen,
    gemahntRechnungen:     gemahnt,
    heuteZeitMin:          heuteMin,
    umsatzMonat:           umsatz,
  };
}

// ── Stat-Karte ─────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = 'blue', icon, onClick }: {
  label: string; value: string; sub?: string;
  color?: 'blue' | 'green' | 'orange' | 'red'; icon: string;
  onClick?: () => void;
}): React.JSX.Element {
  const colors = {
    blue:   'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400',
    green:  'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400',
    orange: 'bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400',
    red:    'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400',
  };
  const inner = (
    <>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${colors[color]}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">{value}</p>
        {sub !== undefined && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </>
  );
  if (onClick !== undefined) {
    return (
      <button onClick={onClick}
        className="card flex items-start gap-4 text-left w-full hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 cursor-pointer">
        {inner}
      </button>
    );
  }
  return <div className="card flex items-start gap-4">{inner}</div>;
}

// ── Dashboard ─────────────────────────────────────────────────────────────

export function Dashboard({ navigate }: { navigate: (m: ModuleKey) => void }): React.JSX.Element {
  const mandant   = useMandantStore(s => s.getAktiverMandant());
  const [kpis, setKpis]       = useState<DashboardKpis | null>(null);
  const [praevKpi, setPraevKpi] = useState<PraeventionKpi | null>(null);
  const [topFaellig, setTopFaellig]         = useState<VguFaelligkeit[]>([]);
  const [topRechnungen, setTopRechnungen]   = useState<Rechnung[]>([]);
  const [maengel, setMaengel]               = useState<OffenerMangel[]>([]);
  const [aufgaben, setAufgaben]             = useState<AkteEintrag[]>([]);
  const [erinnerungen, setErinnerungen]     = useState<Erinnerung[]>([]);
  const [backupAlert, setBackupAlert]       = useState<'none' | 'warn' | 'danger'>('none');
  const [driveNewer,  setDriveNewer]        = useState<string | null>(null);
  const [syncBusy,    setSyncBusy]          = useState(false);
  const now       = new Date();
  const greeting  = now.getHours() < 12 ? 'Guten Morgen' : now.getHours() < 18 ? 'Guten Tag' : 'Guten Abend';
  const monthName = now.toLocaleDateString('de-AT', { month: 'long', year: 'numeric' });

  useEffect(() => {
    void loadKpis(mandant?.id).then(setKpis);
    void getPraeventionKpi(mandant?.id, mandant?.branche ?? undefined, mandant?.mitarbeiterzahl ?? undefined)
      .then(setPraevKpi);
    void loadFaelligkeiten(mandant?.id).then(all => {
      const open = all
        .filter(f => f.status === 'OFFEN' || f.status === 'UEBERFAELLIG')
        .sort((a, b) => a.faelligkeitDatum.localeCompare(b.faelligkeitDatum))
        .slice(0, 5);
      setTopFaellig(open);
    });
    void loadRechnungen().then(all => {
      const gefilt = mandant?.id
        ? all.filter(r => String(r.mandantId) === mandant.id)
        : all;
      const offen = gefilt
        .filter(r => r.status === 'gestellt' || r.status === 'gemahnt')
        .sort((a, b) => a.datum.localeCompare(b.datum))
        .slice(0, 4);
      setTopRechnungen(offen);
    });
    // Begehungs-Mängel (Fristen) + offene Akte-Aufgaben
    void offeneMaengel(mandant?.id).then(m => { setMaengel(m); });
    void loadAkte(mandant?.id).then(all => {
      setAufgaben(all
        .filter(e => (e.naechster_schritt ?? '') !== '' && !(e.erledigt ?? false))
        .slice(0, 5));
    });
    // Manuelle Erinnerungen (Termine/Fristen/ToDos) — geräteübergreifend
    void loadOffeneErinnerungen().then(setErinnerungen);
    // Backup-Warnung — bei aktivem Sync sind die Daten bereits geschützt
    // (zentrale DB + tägliches age-Server-Backup) → kein Drive-Nag.
    void (async () => {
      if (await isSyncConfigured()) { setBackupAlert('none'); return; }
      const last = await getLastBackupTime();
      if (!isDriveConnected()) { setBackupAlert('danger'); return; }
      if (last === null)       { setBackupAlert('warn');   return; }
      const hoursAgo = (Date.now() - new Date(last).getTime()) / 3_600_000;
      setBackupAlert(hoursAgo > 72 ? 'danger' : hoursAgo > 48 ? 'warn' : 'none');
    })();
    // Drive-Sync-Prüfung nur wenn Drive verbunden (sonst kein Nag/Banner)
    if (isDriveConnected()) void checkDriveNewerBackup().then(setDriveNewer);
  }, [mandant?.id]);

  const h = kpis !== null ? Math.floor(kpis.heuteZeitMin / 60) : 0;
  const m = kpis !== null ? kpis.heuteZeitMin % 60 : 0;
  const zeitStr = kpis !== null
    ? (kpis.heuteZeitMin === 0 ? '0 h' : `${h > 0 ? `${h}h ` : ''}${m > 0 ? `${m}m` : ''}`)
    : '…';

  const stats = kpis === null ? [
    { label: 'Fällige Vorsorgen', value: '…', sub: 'Lade…', color: 'orange' as const, icon: '🩺', module: 'vorsorge'   as ModuleKey },
    { label: 'Offene Rechnungen', value: '…', sub: 'Lade…', color: 'red'    as const, icon: '💶', module: 'abrechnung' as ModuleKey },
    { label: 'Heute erfasst',     value: '…', sub: 'Lade…', color: 'blue'   as const, icon: '⏱', module: 'zeit'       as ModuleKey },
    { label: 'Umsatz lfd. Monat', value: '…', sub: 'Lade…', color: 'green'  as const, icon: '📈', module: 'reporting'  as ModuleKey },
  ] : [
    {
      label: 'Fällige Vorsorgen',
      value: String(kpis.vorsorgenFaellig),
      sub: kpis.vorsorgenUeberfaellig > 0 ? `${kpis.vorsorgenUeberfaellig} überfällig` : 'aktuell',
      color: kpis.vorsorgenFaellig > 0 ? 'orange' as const : 'green' as const,
      icon: '🩺',
      module: 'vorsorge' as ModuleKey,
    },
    {
      label: 'Offene Rechnungen',
      value: String(kpis.offeneRechnungen),
      sub: kpis.gemahntRechnungen > 0 ? `${kpis.gemahntRechnungen} gemahnt` : 'keine Mahnungen',
      color: kpis.gemahntRechnungen > 0 ? 'red' as const : kpis.offeneRechnungen > 0 ? 'orange' as const : 'green' as const,
      icon: '💶',
      module: 'abrechnung' as ModuleKey,
    },
    {
      label: 'Heute erfasst',
      value: zeitStr,
      sub: 'Zeit & Leistung',
      color: 'blue' as const,
      icon: '⏱',
      module: 'zeit' as ModuleKey,
    },
    {
      label: 'Umsatz lfd. Monat',
      value: kpis.umsatzMonat > 0
        ? formatEuroCents(kpis.umsatzMonat as EuroCents)
        : '0,00 €',
      sub: monthName,
      color: 'green' as const,
      icon: '📈',
      module: 'reporting' as ModuleKey,
    },
  ];

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {greeting}, Dr. Karrer
        </h1>
        {mandant !== null && mandant !== undefined && (
          <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: mandant.farbe }} />
            {mandant.name}
          </p>
        )}
      </div>

      {/* Backup-Warnung */}
      {backupAlert !== 'none' && (
        <div className={`flex items-start gap-3 rounded-xl px-4 py-3 text-sm border ${
          backupAlert === 'danger'
            ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800/40 text-red-800 dark:text-red-300'
            : 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800/40 text-orange-800 dark:text-orange-300'
        }`}>
          <span className="text-lg flex-shrink-0">{backupAlert === 'danger' ? '🚨' : '⚠️'}</span>
          <div className="flex-1">
            {backupAlert === 'danger' && !isDriveConnected()
              ? <><strong>Kein Backup-Schutz aktiv.</strong> Google Drive nicht verbunden — Daten können durch Browser-Reset verloren gehen.</>
              : <><strong>Backup veraltet.</strong> Das letzte Backup liegt mehr als 48 Stunden zurück.</>
            }
            <span className="ml-2 text-xs opacity-70 cursor-pointer underline"
              onClick={() => { setBackupAlert('none'); }}>
              Schließen
            </span>
          </div>
        </div>
      )}

      {/* Drive-Sync-Banner */}
      {driveNewer !== null && (
        <div className="flex items-start gap-3 rounded-xl px-4 py-3 text-sm border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800/40 text-blue-800 dark:text-blue-300">
          <span className="text-lg flex-shrink-0">☁</span>
          <div className="flex-1">
            <strong>Neuere Daten in Google Drive.</strong>{' '}
            Gespeichert am {new Date(driveNewer).toLocaleString('de-AT', { dateStyle: 'short', timeStyle: 'short' })}.
          </div>
          <button
            disabled={syncBusy}
            onClick={() => {
              if (!window.confirm('Drive-Daten laden und lokale Daten überschreiben?')) return;
              setSyncBusy(true);
              void downloadLatestDriveBackup()
                .then(json => importDexieEncrypted(JSON.parse(json) as Parameters<typeof importDexieEncrypted>[0]))
                .then(() => { window.location.reload(); })
                .catch(() => { setSyncBusy(false); });
            }}
            className="flex-shrink-0 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {syncBusy ? '…' : 'Jetzt sync'}
          </button>
          <button onClick={() => { setDriveNewer(null); }}
            className="flex-shrink-0 text-blue-400 hover:text-blue-600 text-lg leading-none">×</button>
        </div>
      )}

      {/* Überfällige Begehungs-Fristen — prominente Warnung */}
      {maengel.filter(m => m.ueberfaellig).length > 0 && (
        <button onClick={() => { navigate('begehung'); }}
          className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm border text-left
                     bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800/40
                     text-red-800 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/40 transition-colors">
          <span className="text-lg flex-shrink-0">🚨</span>
          <div className="flex-1">
            <strong>{maengel.filter(m => m.ueberfaellig).length} überfällige Mängel-Fristen</strong> aus Begehungen —
            jetzt bearbeiten.
          </div>
          <span className="text-xs flex-shrink-0">Begehung →</span>
        </button>
      )}

      {/* Stats — klickbar: springt ins jeweilige Modul */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <StatCard key={s.label} label={s.label} value={s.value} sub={s.sub}
            color={s.color} icon={s.icon} onClick={() => { navigate(s.module); }} />
        ))}
      </div>

      {/* Präventionszeit KPI — klickbar → Reporting */}
      {praevKpi !== null && (
        <button type="button" onClick={() => { navigate('reporting'); }}
          className={`card flex items-start gap-4 border-l-4 text-left w-full hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 cursor-pointer ${
          praevKpi.farbe === 'green'  ? 'border-l-green-500' :
          praevKpi.farbe === 'orange' ? 'border-l-orange-500' : 'border-l-red-500'
        }`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${
            praevKpi.farbe === 'green'  ? 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400' :
            praevKpi.farbe === 'orange' ? 'bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400' :
                                          'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400'
          }`}>⚕</div>
          <div className="flex-1">
            <p className="text-sm text-gray-500 dark:text-gray-400">Erfüllung Präventionszeit (AsVG)</p>
            <div className="flex items-baseline gap-2 mt-0.5">
              <p className={`text-2xl font-bold leading-tight ${
                praevKpi.farbe === 'green'  ? 'text-green-700 dark:text-green-400' :
                praevKpi.farbe === 'orange' ? 'text-orange-700 dark:text-orange-400' :
                                              'text-red-700 dark:text-red-400'
              }`}>{praevKpi.prozent} %</p>
              <p className="text-xs text-gray-400">
                {praevKpi.istStunden.toFixed(1)} h Ist / {praevKpi.sollStunden.toFixed(1)} h Soll (anteilig)
              </p>
            </div>
            <div className="mt-2 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${
                praevKpi.farbe === 'green'  ? 'bg-green-500' :
                praevKpi.farbe === 'orange' ? 'bg-orange-500' : 'bg-red-500'
              }`} style={{ width: `${Math.min(praevKpi.prozent, 100)}%` }} />
            </div>
          </div>
        </button>
      )}

      {/* 2-Spalten */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Schnellaktionen */}
          <div className="card space-y-3">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Schnellzugriff</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {([
                { label: 'Neue Vorsorge',  icon: '🩺', module: 'vorsorge'   as ModuleKey },
                { label: 'Zeit buchen',    icon: '⏱', module: 'zeit'        as ModuleKey },
                { label: 'Rechnung',       icon: '💶', module: 'abrechnung'  as ModuleKey },
                { label: 'Formular',       icon: '📄', module: 'formulare'   as ModuleKey },
              ]).map(({ label, icon, module }) => (
                <button key={label}
                  onClick={() => { navigate(module); }}
                  className="flex flex-col items-center gap-2 py-4 card hover:border-blue-200
                             dark:hover:border-blue-800 hover:shadow-md transition-all text-center cursor-pointer">
                  <span className="text-2xl">{icon}</span>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Offene Rechnungen — Live */}
          <div className="card space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Offene Rechnungen</h3>
              <button onClick={() => { navigate('abrechnung'); }}
                className="text-xs text-blue-600 hover:underline">alle →</button>
            </div>
            {topRechnungen.length === 0 ? (
              <p className="text-sm text-green-600 dark:text-green-400">✓ Keine offenen Rechnungen</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {topRechnungen.map(r => (
                  <div key={r.rechnungsNr} className="flex items-center justify-between py-2 text-sm">
                    <div>
                      <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded mr-2">
                        {r.rechnungsNr}
                      </span>
                      <span className="text-xs text-gray-500">{r.datum}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
                        {(r.summeBrutto / 100).toLocaleString('de-AT', { minimumFractionDigits: 2 })} €
                      </span>
                      {r.status === 'gemahnt' && (
                        <span className="ml-1.5 text-xs text-red-600 font-medium">gemahnt</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Vorsorge-Fälligkeiten — Live */}
          <div className="card space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Nächste fällige Vorsorgen</h3>
              <button onClick={() => { navigate('vorsorge'); }}
                className="text-xs text-blue-600 hover:underline">alle →</button>
            </div>
            {topFaellig.length === 0 ? (
              <p className="text-sm text-green-600 dark:text-green-400">✓ Keine offenen Vorsorgen</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {topFaellig.map(f => (
                  <div key={f.id} className="flex items-center justify-between py-2 text-sm">
                    <div>
                      <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded mr-2">
                        {f.mitarbeiterKennung}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400 text-xs">{f.tatbestandCode}</span>
                    </div>
                    <span className={`text-xs font-medium ${
                      f.status === 'UEBERFAELLIG'
                        ? 'text-red-600'
                        : 'text-orange-600'
                    }`}>
                      {f.faelligkeitDatum}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Begehungs-Mängel (Fristen) — Live */}
          <div className="card space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Offene Mängel (Begehungen)</h3>
              <button onClick={() => { navigate('begehung'); }}
                className="text-xs text-blue-600 hover:underline">alle →</button>
            </div>
            {maengel.length === 0 ? (
              <p className="text-sm text-green-600 dark:text-green-400">✓ Keine offenen Mängel</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {maengel.slice(0, 5).map(m => (
                  <div key={m.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <div className="min-w-0">
                      <p className="text-gray-800 dark:text-gray-200 truncate">{m.beschreibung || '—'}</p>
                      <p className="text-xs text-gray-400 truncate">{m.bereich || 'ohne Bereich'}</p>
                    </div>
                    <span className={`text-xs font-medium flex-shrink-0 ${m.ueberfaellig ? 'text-red-600' : 'text-orange-600'}`}>
                      {m.frist !== undefined && m.frist !== ''
                        ? `${m.ueberfaellig ? '⚠ ' : ''}${m.frist}`
                        : PRIORITAET_META[m.prioritaet].label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Offene Aufgaben (Akte / KI-Vorschläge) — Live */}
          <div className="card space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Offene Aufgaben</h3>
              <button onClick={() => { navigate('akte'); }}
                className="text-xs text-blue-600 hover:underline">Akte →</button>
            </div>
            {aufgaben.length === 0 ? (
              <p className="text-sm text-green-600 dark:text-green-400">✓ Keine offenen Aufgaben</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {aufgaben.map(a => (
                  <div key={a.id} className="flex items-center gap-2 py-2 text-sm">
                    <span className="flex-shrink-0">🎯</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-gray-800 dark:text-gray-200 truncate">{a.naechster_schritt}</p>
                      <p className="text-xs text-gray-400 truncate">{a.titel}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Erinnerungen (manuell gesetzt, per Telegram) — Live, geräteübergreifend */}
          <div className="card space-y-3">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">🔔 Erinnerungen</h3>
            {erinnerungen.length === 0 ? (
              <p className="text-sm text-gray-400">Keine Erinnerungen gesetzt. Setze welche mit dem 🔔 an Terminen, Fristen und Aufgaben.</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {erinnerungen.slice(0, 6).map(e => {
                  const faellig = e.remind_datum <= new Date().toISOString().slice(0, 10);
                  const [y, m, d] = e.remind_datum.split('-');
                  return (
                    <div key={e.id} className="flex items-center gap-2 py-2 text-sm">
                      <input type="checkbox" checked={false}
                        onChange={() => { void toggleErinnerungErledigt(e.id).then(() => loadOffeneErinnerungen().then(setErinnerungen)); }}
                        className="rounded border-gray-300 text-green-600 flex-shrink-0" title="Als erledigt markieren" />
                      <div className="min-w-0 flex-1">
                        <p className="text-gray-800 dark:text-gray-200 truncate">{e.titel}</p>
                      </div>
                      <span className={`text-xs flex-shrink-0 ${faellig ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                        {faellig ? '⏰ ' : ''}{d}.{m}.{y}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* System-Status */}
        <div className="card space-y-2">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">System</h3>
          <div className="space-y-1.5 text-xs">
            {[
              ['Krypto',        '✅ AES-256-GCM'],
              ['Datenbank',     '✅ Dexie v10'],
              ['Multi-Device',  '✅ Sync aktiv'],
              ['PWA / Offline', '✅ Service Worker'],
              ['Smart-Matching','✅ aktiv'],
              ['Module',        '✅ 16 live'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-gray-500">{k}</span>
                <span className="text-gray-700 dark:text-gray-300">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
